/**
 * Centralised HTML sanitiser for any user-authored content rendered via
 * `dangerouslySetInnerHTML`. Strips <script> tags, on* event handlers,
 * `javascript:` URLs and other XSS vectors while keeping the rich-text
 * subset our composer produces (formatting, lists, tables, images, links).
 */
import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'a', 'b', 'i', 'em', 'strong', 'u', 's',
  'p', 'br', 'span', 'div', 'blockquote',
  'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'code', 'pre',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'img',
  'hr',
];

const ALLOWED_ATTR = [
  'href', 'target', 'rel', 'src', 'alt', 'title',
  'class', 'style',
  'width', 'height',
  'colspan', 'rowspan',
  // Marker used to detect+swap the signature block when the "Reply as"
  // dropdown changes. Kept in a data-* attribute so it survives sanitisation.
  'data-signature',
];

const CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  // Allow data: only for images (e.g. base64 paste previews); strip anything else.
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cid):|data:image\/|\/|#)/i,
  // Always set rel="noopener noreferrer" on links with target so we never leak.
  ADD_ATTR: ['target'],
};

if (typeof window !== 'undefined' && DOMPurify && DOMPurify.addHook) {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('target')) {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

/** Sanitize an HTML string for safe rendering via dangerouslySetInnerHTML. */
export function sanitizeHtml(html) {
  if (typeof html !== 'string' || !html) return '';
  try {
    return DOMPurify.sanitize(html, CONFIG);
  } catch {
    return '';
  }
}

/** Strip ALL HTML tags and return plain text. Safe for use in any context. */
export function stripHtmlSafe(html) {
  if (typeof html !== 'string' || !html) return '';
  try {
    // Use DOMParser so no script is ever executed (unlike innerHTML which
    // triggers <img onerror=> on attach).
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body && (doc.body.textContent || doc.body.innerText) || '').trim();
  } catch {
    return '';
  }
}

export default sanitizeHtml;
